import { useEffect } from 'react'

import { useRouter } from 'next/compat/router'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, pxToRem } from '@b-stock/fe-core/design-system'

import BreadcrumbsSection from '@/buyer-portal/components/features/listing/BreadcrumbsSection'
import ContractMaoTaxBannerContainer from '@/buyer-portal/components/features/listing/ContractMaoTaxBanner'
import { FirstDibsOpensBanner } from '@/buyer-portal/components/features/listing/FirstDibsBanner'
import { useRecordListingView } from '@/buyer-portal/components/features/listing/hooks/useRecordListingView'
import { useTrackPageViewed } from '@/buyer-portal/components/features/listing/hooks/useTrackPageViewed'
import { ListingActionColumn } from '@/buyer-portal/components/features/listing/ListingActionColumn'
import { MakeAnOfferCard } from '@/buyer-portal/components/features/listing/MakeAnOffer'
import { ListingMediaCard } from '@/buyer-portal/components/features/listing/media/ListingMediaCard'
import MediaSection from '@/buyer-portal/components/features/listing/media/MediaSection'
import { OfferingInfoCard } from '@/buyer-portal/components/features/listing/OfferingInfo'
import RunningVatEstimateContainer from '@/buyer-portal/components/features/listing/RunningVatEstimate'
import {
  withScrollIntoView,
  ScrollIntoViewId,
  useScrollIntoView,
} from '@/buyer-portal/components/features/listing/ScrollIntoView/ScrollIntoViewContext'
import { SellerCollectsBanner } from '@/buyer-portal/components/features/listing/SellerCollectsBanner'
import { UnAuthWarningCard } from '@/buyer-portal/components/features/unAuthWarning'
import { withUnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import { MoreFromSeller } from '@/components/listings/MoreFromSeller'
import { NotesPreviewWidget } from '@/components/listings/notes'
import { RecentlyViewedCarousel } from '@/components/listings/RecentlyViewedCarousel'
import { LdpPersonalizedCarousel } from '@/components/personalizedCategoryCarousel/LdpPersonalizedCarousel'
import { FeatureFlags } from '@/helpers/featureFlags'

import { ListingAttachmentsCard } from '../ListingAttachments'
import { ListingDetailsCard } from '../ListingDetailsCard'
import { ListingManifestSection } from '../ListingManifestSection'
import { LotDescriptionCard } from '../LotDescription'
import { ListingPoliciesCard } from '../Policies'
import { SearchSimilarListings } from '../SearchSimilarListings'
import { ShipmentDetailsCard } from '../Shipment'
import { FacilityStatsCard } from './facilityStats'
import { HazmatInfoBanner } from './HazmatInfoBanner'
import { TitleCard } from './TitleCard'
import { useListingDetailsLayoutTracking } from './useListingDetailsLayoutTracking'

const Body = styled.div``

/*
 * The breadcrumb eyebrow uses bespoke padding (not the shared 1rem gap rhythm):
 * 8px top/bottom on desktop, 12px on mobile. On mobile the breadcrumb row also
 * carries its own 8px padding and a bottom divider, so the line sits between the
 * breadcrumb (8px) and the eyebrow's bottom padding (12px).
 */
const Eyebrow = styled.div`
  margin-top: -1rem;
  padding: 0.5rem 0;

  @media ${Breakpoints.max.medium} {
    padding: 0.75rem 0;
  }
`

const BreadcrumbsBar = styled.div`
  @media ${Breakpoints.max.medium} {
    border-bottom: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
    padding: 0.5rem 0;
  }
`

/*
 * Two independent desktop columns (left = blocks 1,3,5; right = blocks 2,4).
 * On mobile the column wrappers collapse via `display: contents` so all five
 * blocks become direct flex children of the row, and their `order` interleaves
 * them into a single column in visual order 1 → 5.
 */
const Columns = styled.div`
  align-items: flex-start;
  display: flex;
  flex-flow: row nowrap;
  gap: 1rem;

  @media ${Breakpoints.max.medium} {
    align-items: stretch;
    flex-direction: column;
    gap: 0.75rem;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex: 5 1 0%;
  flex-direction: column;
  gap: 1rem;
  min-width: 25rem;

  @media ${Breakpoints.max.medium} {
    display: contents;
  }
`

const RightColumn = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
  gap: 1rem;
  width: 27.75rem;

  @media ${Breakpoints.max.medium} {
    display: contents;
  }
`

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;

  @media ${Breakpoints.max.medium} {
    gap: 0.75rem;
  }

  &:empty {
    display: none;
  }
`

const Block1 = styled(Block)`
  order: 1;
`
const Block2 = styled(Block)`
  order: 2;
`
const Block3 = styled(Block)`
  order: 3;
`
const Block4 = styled(Block)`
  order: 4;
`
const Block5 = styled(Block)`
  order: 5;
`

const ListingDetailsLayoutV2Base = ({
  initialBidAmount,
  listing,
  referralUrl = '',
}: {
  initialBidAmount?: string
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
  referralUrl?: string
}) => {
  const router = useRouter()
  const isLoggedIn = !!useDecodedAccessToken()?.personId
  const mediaGalleryVariant = useFlag(FeatureFlags.LDP_V2_MEDIA_GALLERY_AB)

  const { refs } = useScrollIntoView()

  useEffect(() => {
    if (!router?.isReady) return

    if (router.asPath.includes('#manifest-section')) {
      refs[ScrollIntoViewId.MANIFEST]?.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [router?.isReady, router?.asPath, refs])

  useTrackPageViewed({ listing, referralUrl })
  useListingDetailsLayoutTracking({ listing })
  useRecordListingView({ listingId: listing._id })

  return (
    <Body>
      <Eyebrow>
        <BreadcrumbsBar>
          <BreadcrumbsSection listing={listing} />
        </BreadcrumbsBar>
      </Eyebrow>

      <FirstDibsOpensBanner listing={listing} />

      <Columns>
        <LeftColumn>
          <Block1>
            <TitleCard listing={listing} />
            <OfferingInfoCard listing={listing} />
            {mediaGalleryVariant === 'v1' ? (
              <MediaSection listing={listing} />
            ) : (
              <ListingMediaCard listing={listing} />
            )}
          </Block1>

          <Block3>
            <ListingManifestSection listing={listing} />
            <MakeAnOfferCard listing={listing} />
          </Block3>

          <Block5>
            <HazmatInfoBanner listing={listing} />
            <ListingDetailsCard listing={listing} />
            {isLoggedIn ? (
              <>
                <LotDescriptionCard listing={listing} />
                <ShipmentDetailsCard listing={listing} />
                <ListingPoliciesCard listing={listing} />
                <ListingAttachmentsCard listing={listing} />
              </>
            ) : (
              <UnAuthWarningCard
                reason={<FormattedMessage id="Listing.seeMoreListingDetails" />}
              />
            )}
            <MoreFromSeller listing={listing} variant="card" />
            <RecentlyViewedCarousel excludeListingId={listing._id} />
            <LdpPersonalizedCarousel
              excludeListingId={listing._id}
              listingStorefrontId={listing.storefrontId}
            />
          </Block5>
        </LeftColumn>

        <RightColumn>
          <Block2>
            <SellerCollectsBanner listing={listing} />
            <ListingActionColumn
              initialBidAmount={initialBidAmount}
              listing={listing}
            />
            <ContractMaoTaxBannerContainer listing={listing} />
            <RunningVatEstimateContainer listing={listing} />
          </Block2>

          <Block4>
            <NotesPreviewWidget listing={listing} />
            <FacilityStatsCard listing={listing} />
            <SearchSimilarListings listing={listing} variant="card" />
          </Block4>
        </RightColumn>
      </Columns>
    </Body>
  )
}

export const ListingDetailsLayoutV2 = withScrollIntoView(
  withUnAuthWarningModal(ListingDetailsLayoutV2Base)
)
